import * as React from 'react'

import {
  InlineEditTextFieldStyled,
  StatusIconWrapperStyled,
} from './styles/InlineEditTextFieldStyled'
import { IconAttention, IconCheck, IconEdit } from '@ui/elements/Icon'
import { CircleLoader } from '@ui/elements/Loaders'

export type InlineEditTextFieldStatus =
  | 'idle'
  | 'pending'
  | 'resolved'
  | 'rejected'

export interface InlineEditTextFieldStatusAriaLabels {
  idle: string
  pending: string
  resolved: string
  rejected: string
}

export interface IInlineEditTextFieldProps {
  value: string
  status: InlineEditTextFieldStatus
  onChange: (value: string) => void
  onBlur?: () => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  statusAriaLabels?: InlineEditTextFieldStatusAriaLabels
  placeholder?: string
  ariaLabel?: string
  maxLength?: number
  disabled?: boolean
  fullWidth?: boolean
  analyticsId?: string
}

const renderStatusIcon = (
  status: InlineEditTextFieldStatus,
  ariaLabels: InlineEditTextFieldStatusAriaLabels
) => {
  if (status === 'pending') {
    return (
      <StatusIconWrapperStyled aria-label={ariaLabels.pending}>
        <CircleLoader size={16} color='#999' strokeWidth={2} />
      </StatusIconWrapperStyled>
    )
  }

  if (status === 'resolved') {
    return (
      <StatusIconWrapperStyled>
        <IconCheck color='success' size={16} ariaLabel={ariaLabels.resolved} />
      </StatusIconWrapperStyled>
    )
  }

  if (status === 'rejected') {
    return (
      <StatusIconWrapperStyled>
        <IconAttention
          color='danger'
          size={16}
          ariaLabel={ariaLabels.rejected}
        />
      </StatusIconWrapperStyled>
    )
  }

  // idle
  return (
    <StatusIconWrapperStyled>
      <IconEdit color='textSecondary' size={16} ariaLabel={ariaLabels.idle} />
    </StatusIconWrapperStyled>
  )
}

export const InlineEditTextField = ({
  value,
  status,
  onChange,
  onBlur,
  onKeyDown,
  statusAriaLabels = {
    idle: 'Edit',
    pending: 'Saving',
    resolved: 'Saved',
    rejected: 'Failed to save',
  },
  placeholder,
  ariaLabel,
  maxLength,
  disabled,
  fullWidth = true,
  analyticsId,
}: IInlineEditTextFieldProps) => {
  return (
    <InlineEditTextFieldStyled
      analyticsId={analyticsId}
      placeholder={placeholder}
      value={value}
      fullWidth={fullWidth}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      disabled={disabled}
      inputProps={{
        'aria-label': ariaLabel,
        ...(maxLength !== undefined && { maxLength }),
      }}
      InputProps={{
        endAdornment: renderStatusIcon(status, statusAriaLabels),
      }}
    />
  )
}

InlineEditTextField.displayName = 'InlineEditTextField'
